import { useState, useLayoutEffect, useEffect } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';

import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { useMedia } from '@hooks/use-media';

import { Box } from 'components/Box';
import { Loading } from 'components/Loading';
import { PageContent } from 'components/Page';
import { useStore } from 'models/Provider';

import { ApplicantList } from './ApplicantList';
import Application from './Application';
import { ApplicationMoveCopy } from './ApplicationMoveCopy';
import { ApplicationPageHeader } from './ApplicationPageHeader';
import GDPRApplication from './GDPRApplication';

export const ApplicationContainer = observer(() => {
  const [error, setError] = useState(false);
  const { applicationID, jobID } = useParams();
  const { applicationStore, jobStore, t } = useStore();
  const showApplicantList = useMedia('(min-width: 1150px)');
  const job = jobStore.items.get(jobID);
  const application = applicationStore.items.get(applicationID);
  useEffect(() => {
    if (application) {
      application.setUserRead('newApplication');
      application.setUserRead('assignedTo');
    }
  }, [application]);

  useLayoutEffect(() => {
    jobStore.loadJob(jobID);
  }, [jobStore, jobID]);

  useLayoutEffect(() => {
    applicationStore.load(applicationID).then((success) => {
      if (!success) setError(true);
    });
  }, [applicationID, applicationStore, jobID]);

  if (error) {
    return <Navigate to={`/jobs/${jobID}`} />;
  }

  if (!application || !job) {
    return <Loading />;
  }

  if (application.contentCleared) {
    return <GDPRApplication t={t} />;
  }

  return (
    <Routes>
      <Route path="move" element={<ApplicationMoveCopy application={application} move />} />
      <Route path="copy" element={<ApplicationMoveCopy application={application} />} />
      <Route
        path="*"
        element={
          <>
            <ApplicationPageHeader application={application} />
            <PageContent alwaysScroll>
              <Box flex grow justifyContent="flex-end">
                <Application job={job} application={application} />
                {showApplicantList && (
                  <ApplicantListContainer>
                    <ApplicantListFixed>
                      <ApplicantList selectedApplication={application} job={job} />
                    </ApplicantListFixed>
                  </ApplicantListContainer>
                )}
              </Box>
            </PageContent>
          </>
        }
      />
    </Routes>
  );
});

export default ApplicationContainer;

const ApplicantListContainer = styled.div((p) => ({
  minWidth: '17.5rem',
  width: '17.5rem',
  marginLeft: p.theme.spacing.m40,
  position: 'relative',
}));

const ApplicantListFixed = styled.div({
  position: 'fixed',
  width: '100%',
  maxWidth: '17.5rem',
});
